import { FC } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSessionStorage } from 'usehooks-ts';

import {
  SaveToolButton,
  SaveToolButtonCallbacks,
  SearchHistoryEntry,
} from '@app/components';
import { useAppContext } from '@app/contexts';
import {
  usePatronSession,
  UserSearchDto,
  useSaveSearchesMutation,
  useUnsaveSearchesMutation,
} from '@app/hooks';
import { addEntryToDashboardList } from '@app/utils';
import { ShapeWithTotalRecords, storageKeys } from '@app/constants';

type SavedSearchesDto = ShapeWithTotalRecords<{
  savedSearches: UserSearchDto[];
}>;

interface SavedInstanceToolButtonProps {
  entry: SearchHistoryEntry;
  savedSearchesList: SearchHistoryEntry[];
  isItemRemovable?: boolean;
}

export const SavedSearchToolButton: FC<SavedInstanceToolButtonProps> = ({
  entry,
  savedSearchesList,
  isItemRemovable = false,
}) => {
  const queryClient = useQueryClient();
  const { isLoggedIn } = useAppContext();
  const { externalSystemId } = usePatronSession();
  const [savedSearches, setSavedSearches] = useSessionStorage<UserSearchDto[]>(
    storageKeys.savedSearches,
    []
  );
  const saveSearchMutation = useSaveSearchesMutation();
  const unsaveSearchMutation = useUnsaveSearchesMutation();

  const savedSearchItemsList = isLoggedIn ? savedSearchesList : savedSearches;
  const itemTimestampKeyName = isLoggedIn ? 'timestamp' : 'date';
  const isSelected = savedSearchItemsList.some(
    savedSearch => savedSearch[itemTimestampKeyName] === entry.timestamp
  );
  const newEntry = {
    query: entry.query as string,
    searchType: entry.searchType,
    requestParams: entry.requestParams as string,
    date: entry.timestamp,
    resultCount: entry.totalRecords,
    interface: entry.searchInterface,
    id: entry.id,
  };

  const removeSavedSearchFromQueryCache = (idToDelete: string) => {
    if (isItemRemovable) {
      queryClient.setQueryData(
        ['savedSearches', externalSystemId],
        (previousData: SavedSearchesDto) => ({
          ...previousData,
          savedSearches: previousData.savedSearches.filter(
            ({ id }) => id !== idToDelete
          ),
        })
      );
    }
  };

  const handleAuthorizedAction = ({
    setItemSelected,
    showNotification,
  }: SaveToolButtonCallbacks) => {
    if (isSelected) {
      const savedSearchId = savedSearchesList.find(
        item => item.timestamp === entry.timestamp
      )?.id;
      const entryId = entry.id as string;

      if (savedSearchId) {
        unsaveSearchMutation.mutate(savedSearchId, {
          onSuccess: () => {
            removeSavedSearchFromQueryCache(entryId);
          },
          onError: () => {
            setItemSelected(true);
            showNotification({ isSelected: true, isError: true });
          },
        });
      }
    } else {
      saveSearchMutation.mutate(
        { savedSearch: newEntry },
        {
          onError: () => {
            setItemSelected(false);
            showNotification({ isSelected: false, isError: true });
          },
        }
      );
    }
  };

  const handleUnauthorizedAction = ({
    showNotification,
  }: SaveToolButtonCallbacks) => {
    showNotification({ isSelected });

    setSavedSearches(prevState =>
      isSelected
        ? prevState.filter(savedSearch => savedSearch.date !== entry.timestamp)
        : addEntryToDashboardList(newEntry, prevState)
    );
  };

  return (
    <SaveToolButton
      isSelected={isSelected}
      isDisabled={
        saveSearchMutation.isLoading || unsaveSearchMutation.isLoading
      }
      onAuthorizedAction={handleAuthorizedAction}
      onUnauthorizedAction={handleUnauthorizedAction}
    />
  );
};
